import { useState, useEffect } from 'react';
import { X, Share, Plus, MoreVertical, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

export const AddToHomeScreenPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    // Check if already dismissed
    const dismissed = localStorage.getItem('homescreen_prompt_dismissed');
    if (dismissed) return;

    // Check if running as standalone (already installed)
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone === true) return;

    // Check if mobile
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile = /iphone|ipad|ipod|android/.test(userAgent);
    
    if (!isMobile) return;

    // Detect platform
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    const isAndroidDevice = /android/.test(userAgent);
    
    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);

    // Show prompt after a short delay
    const timer = setTimeout(() => {
      setShowPrompt(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('homescreen_prompt_dismissed', 'true');
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 max-w-md mx-auto">
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-forest/10 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-forest" />
          </div>
          
          <div className="flex-1 pr-4">
            <h3 className="font-semibold text-gray-900 mb-1">Add to Home Screen</h3>
            <p className="text-sm text-gray-600 mb-3">
              Install EquiVault for quick access - just like a regular app!
            </p>
            
            {isIOS && (
              <div className="text-xs text-gray-500 space-y-1">
                <p className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded bg-gray-100 flex items-center justify-center">
                    <Share className="w-3 h-3" />
                  </span>
                  Tap the <strong>Share</strong> button below
                </p>
                <p className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded bg-gray-100 flex items-center justify-center">
                    <Plus className="w-3 h-3" />
                  </span>
                  Then tap <strong>"Add to Home Screen"</strong>
                </p>
              </div>
            )}
            
            {isAndroid && (
              <div className="text-xs text-gray-500 space-y-1">
                <p className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded bg-gray-100 flex items-center justify-center">
                    <MoreVertical className="w-3 h-3" />
                  </span>
                  Tap the <strong>menu</strong> (⋮) in Chrome
                </p>
                <p className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded bg-gray-100 flex items-center justify-center">
                    <Plus className="w-3 h-3" />
                  </span>
                  Then tap <strong>"Add to Home screen"</strong>
                </p>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex gap-2 mt-3 pt-3 border-t">
          <Button 
            variant="ghost" 
            size="sm" 
            className="flex-1 text-gray-500"
            onClick={handleDismiss}
          >
            Maybe Later
          </Button>
          <Button 
            size="sm" 
            className="flex-1 bg-forest hover:bg-forest-dark"
            onClick={handleDismiss}
          >
            Got It!
          </Button>
        </div>
      </div>
    </div>
  );
};
